import { EventEmitter } from 'events';
import * as request from './utils/request';
import triggers, { getParentByDataAttribute } from './triggers';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { IntegrationConfig } from './interfaces';
import {
  getEvoIbePreviewConfig,
  getNoEvoRedirectParam,
  removeQueryParamPreservingEncoding
} from './utils/url';
import {
  loadEvoPreview,
  removeEvoPreviewFailure,
  showEvoPreviewFailure,
  EvoPreviewError
} from './features/evoPreview';

declare global {
  interface ClientConfig {
    aiKey: string;
    dbmUrl: string;
    dbmNewUrl: string;
    evoIbeUrl: string;
    cdnEditorDomain: string;
    configCatKey: string;
    editorDomain: string;
  }

  interface Window {
    oc: any;
    ENV_DATA: Readonly<ClientConfig>;
    guestlineEmitter: any;
    guestlineEvents: {
      on: (event: string, callback: (...args: any[]) => void) => void;
      off: (event: string, callback: (...args: any[]) => void) => void;
      onRedirectButtonClick: (callback: Function) => void;
      onPanelButtonClick: (callback: Function) => void;
    };
    ga: any;
    gtag: any;
    google_tag_manager: any;
    google_tag_data: any;
    dataLayer: any;
    GLAppInsights: ApplicationInsights;
    dbm: any;
    glDBMTagReady: boolean;
    guestlineQueue: Array<() => void>;
    guestlineReady: (callback: () => void) => void;
  }
}

export type Language = 'en' | 'de' | 'nl' | 'pt' | 'es' | 'it';

const debugEnabled = window.location.search.includes('dbm-debug=');
const CDN_EDITOR_DOMAIN = debugEnabled
  ? (window.ENV_DATA || {}).editorDomain || process.env.EDITOR_DOMAIN
  : (window.ENV_DATA || {}).cdnEditorDomain || process.env.CDN_EDITOR_DOMAIN;

const definedOnly = (values: Record<string, unknown> = {}) =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));

/**
 * Editor's configs, with the settings Config App owns taken from EVO IBE's public config
 * for engines Config App publishes. Editor keeps the widget, basket and dropdown settings,
 * which Config App does not own; if the public config is unavailable, Editor's stand.
 */
async function loadConfigs(collectionId: string, evoIbeDomain?: string) {
  const [editor, published] = await Promise.all([
    request.get(
      `${CDN_EDITOR_DOMAIN}/api/collections/${collectionId}/configs?sections[]=integration&sections[]=analytics&sections[]=dbm&sections[]=branding`
    ),
    evoIbeDomain
      ? Promise.resolve()
          .then(() => request.get(`https://${evoIbeDomain}/public-config/${collectionId}`))
          .catch(() => null)
      : null
  ]);
  if (published?.source !== 'blob') return editor;

  const { newTabRedirect, useEvo, panelEnabled, panelButtonEnabled, panelFullscreen } =
    published.integration;
  return {
    ...editor,
    integration: {
      ...editor.integration,
      ...definedOnly({ newTabRedirect, useEvo, panelEnabled, panelButtonEnabled, panelFullscreen })
    },
    analytics: { ...editor.analytics, ...definedOnly(published.analytics) },
    branding: { ...editor.branding, ...definedOnly(published.branding) }
  };
}

window.guestlineQueue = window.guestlineQueue || [];
let evoPreviewReadyTag: HTMLScriptElement | null = null;

function flushGuestlineQueue(): void {
  const callbacks = window.guestlineQueue;
  window.guestlineQueue = [];
  callbacks.forEach(callback => {
    try {
      callback();
    } catch (error) {
      console.error('EVO preview readiness callback failed', error);
    }
  });
}

function markEvoPreviewReady(tag: HTMLScriptElement): void {
  evoPreviewReadyTag = tag;
  flushGuestlineQueue();
}

window.guestlineReady = callback => {
  const currentTag = document.getElementById('guestline-tag') as HTMLScriptElement | null;
  const previewReady = evoPreviewReadyTag === currentTag;
  if (window.guestlineEvents || previewReady) {
    callback();
  } else {
    window.guestlineQueue.push(callback);
  }
};

async function tagInit() {
  const tag = document.getElementById('guestline-tag') as HTMLScriptElement | null;
  const page = window.location.href
    ? removeQueryParamPreservingEncoding(window.location.href, 'evoIbePreview')
    : 'Page info not available';

  if (tag === null) {
    window.GLAppInsights.trackTrace({
      message: 'Tag Not Found',
      properties: { page }
    });
    return console.warn(
      'The tag might not be implemented correctly. Please refer to the documentation for more information'
    );
  }

  if (getEvoIbePreviewConfig(window.location.search)) {
    let lateReady = false;
    const handleLateReady = () => {
      lateReady = true;
      removeEvoPreviewFailure();
      markEvoPreviewReady(tag);
    };

    try {
      await loadEvoPreview(tag, handleLateReady);
      markEvoPreviewReady(tag);
    } catch (error) {
      const code = error instanceof EvoPreviewError ? error.code : 'unknown';
      console.error(`EVO preview delegation failed (${code})`);
      showEvoPreviewFailure(tag);
      if (lateReady) removeEvoPreviewFailure();
      window.GLAppInsights?.trackException({ exception: new Error('EVO preview delegation failed') });
    }
    return;
  }

  const triggerEventEmitter = new EventEmitter();

  window.guestlineEvents = {
    on: (event: string, callback: (...args: any[]) => void) => {
      triggerEventEmitter.on(event, callback);
    },
    off: (event: string, callback: (...args: any[]) => void) => {
      triggerEventEmitter.off(event, callback);
    },
    onRedirectButtonClick: (callback: Function) => {
      triggerEventEmitter.on('clicked', event => {
        const element = event.target;
        if (element.hasAttribute('data-guestline-redirect')) {
          callback(event);
        }
      });
    },
    onPanelButtonClick: (callback: Function) => {
      triggerEventEmitter.on('clicked', event => {
        const element = getParentByDataAttribute(
          event.target as Element,
          'data-guestline-show-panel'
        );
        if (element) {
          callback(event);
        }
      });
    }
  };

  const collectionId = tag.getAttribute('data-group-id')!;
  const lng = (tag.getAttribute('data-lng') || window.navigator.language || 'en') as Language;
  const hotelId = tag.getAttribute('data-hotel-id') || undefined;

  const newDomain = (window.ENV_DATA || {}).dbmNewUrl || process.env.DBM_NEW_URL;
  const evoIbeDomain = (window.ENV_DATA || {}).evoIbeUrl || process.env.EVO_IBE_URL;
  const dbmBaseUrl = `https://${newDomain}/${collectionId.toUpperCase()}`;
  const evoIbeBaseUrl = `https://${evoIbeDomain}/${collectionId.toUpperCase()}`;

  window.GLAppInsights.trackTrace({
    message: 'Tag Initiated',
    properties: { page, collectionId }
  });

  try {
    const { integration, analytics, dbm, branding } = await loadConfigs(
      collectionId,
      evoIbeDomain
    );

    window.GLAppInsights.trackTrace({
      message: 'Tag config',
      properties: { integration, collectionId }
    });

    const shouldStayInDbm = getNoEvoRedirectParam().noEvoRedirect === 'true';
    const effectiveIntegration = shouldStayInDbm
      ? { ...integration, useEvo: false }
      : integration;
    const baseUrl = effectiveIntegration.useEvo === true ? evoIbeBaseUrl : dbmBaseUrl;

    if (window.guestlineEmitter) {
      import('./features/widget').then(({ default: loadWidgetFeature }) => {
        window.guestlineEmitter.on(
          'widget-render',
          (editorIntegrationConfig: IntegrationConfig, collectionId: string) => {
            loadWidgetFeature(editorIntegrationConfig, dbm, collectionId, hotelId);
          }
        );
        window.guestlineEmitter.emit('widget-ready');
      });
    } else {
      triggers(
        { integration: effectiveIntegration, analytics, lng, hotelId },
        baseUrl,
        triggerEventEmitter
      );

      if (effectiveIntegration.panelEnabled) {
        const { default: loadPanelFeature } = await import('./features/panel');

        loadPanelFeature(baseUrl, collectionId, triggerEventEmitter, {
          branding,
          analytics,
          integration: effectiveIntegration,
          hotelId,
          lng
        });
      } else if (effectiveIntegration.widgetEnabled) {
        const { default: loadWidgetFeature } = await import('./features/widget');
        loadWidgetFeature(effectiveIntegration, dbm, collectionId, hotelId);
      }

      if (effectiveIntegration.basketEnabled) {
        const { default: loadBasketTransferFeature } = await import('./features/basketTransfer');
        loadBasketTransferFeature(baseUrl, effectiveIntegration, triggerEventEmitter);
      }
    }
  } catch (err) {
    console.error(err);
    window.GLAppInsights.trackException({ exception: err as Error });
  }

  window.guestlineQueue.forEach(callback => callback());
  window.guestlineQueue = [];
}

export default async function main() {
  import('./monitoring')
    .then(async ({ default: monitoringInit }) => {
      monitoringInit();
      tagInit();
    })
    .catch(() => {
      tagInit();
    });
}
